import { ColorRule, ColorRuleOperator } from 'src/app/core/dto';

/** A field a color rule can read (source) or paint (target). */
export interface ColorRuleField {
  /** Canonical field name, matching the row property on every surface. */
  field: string;
  /** Human label for the editor. */
  label: string;
}

/**
 * Canonical fields offered in the rule editor. These names resolve identically
 * on the scheduler job items and the orders-list rows. A target field only
 * paints on surfaces where a column for it exists; that is harmless.
 */
export const COLOR_RULE_FIELDS: ColorRuleField[] = [
  { field: 'materialCode', label: 'Material' },
  { field: 'materialGauge', label: 'Gauge' },
  { field: 'materialWidthIn', label: 'Width' },
  { field: 'materialColor', label: 'Color' },
  { field: 'materialDescription', label: 'Material Description' },
  { field: 'toolingCode', label: 'Tooling' },
  { field: 'toolingDescription', label: 'Tooling Description' },
  { field: 'orderCode', label: 'Order' },
  { field: 'customerName', label: 'Customer' },
  { field: 'status', label: 'Status' },
  { field: 'truckNumber', label: 'Truck' },
];

/** Operators exposed in the v1 editor. See specs/color-rules.md. */
export const COLOR_RULE_OPERATORS: { value: ColorRuleOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'in', label: 'is one of' },
  { value: 'between', label: 'is between' },
];

/**
 * Shared, framework-agnostic color-rule engine. See `specs/color-rules.md`.
 *
 * This module has zero Angular / grid dependencies on purpose: the scheduler
 * grids call it at tree-build time and the orders list (ag-Grid) calls it from
 * a `cellStyle` callback. Any future surface adapts the same pure functions.
 */

/** Coerce a value to a number, or `null` if it isn't numeric. */
function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? null : n;
}

/** Case-insensitive string comparison of two values. */
function stringEquals(a: unknown, b: unknown): boolean {
  return String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();
}

/**
 * Compare two values for equality. If both are numeric they compare as numbers
 * (so `22` matches `"22"` and `"22.0"`); otherwise they compare as
 * case-insensitive strings.
 */
function looseEquals(a: unknown, b: unknown): boolean {
  const an = asNumber(a);
  const bn = asNumber(b);
  if (an !== null && bn !== null) {
    return an === bn;
  }
  return stringEquals(a, b);
}

/** Does a single rule's condition match the given cell value? */
export function ruleMatches(rule: ColorRule, cellValue: unknown): boolean {
  switch (rule.operator) {
    case 'equals':
      return looseEquals(cellValue, rule.value);
    case 'notEquals':
      return !looseEquals(cellValue, rule.value);
    case 'contains':
      return String(cellValue ?? '')
        .toLowerCase()
        .includes(String(rule.value ?? '').toLowerCase());
    case 'in':
      return (rule.values ?? []).some((v) => looseEquals(cellValue, v));
    case 'lessThan':
    case 'lessThanOrEqual':
    case 'greaterThan':
    case 'greaterThanOrEqual': {
      const x = asNumber(cellValue);
      const y = asNumber(rule.value);
      if (x === null || y === null) {
        return false;
      }
      if (rule.operator === 'lessThan') return x < y;
      if (rule.operator === 'lessThanOrEqual') return x <= y;
      if (rule.operator === 'greaterThan') return x > y;
      return x >= y;
    }
    case 'between': {
      const x = asNumber(cellValue);
      const lo = asNumber(rule.value);
      const hi = asNumber(rule.value2);
      if (x === null || lo === null || hi === null) {
        return false;
      }
      // Tolerate bounds given in either order.
      const min = Math.min(lo, hi);
      const max = Math.max(lo, hi);
      return x >= min && x <= max;
    }
    default:
      return false;
  }
}

/**
 * Evaluate the color for a single cell.
 *
 * @param row          The full row data (source-field values are read from here).
 * @param targetField  The canonical field name of the cell being colored.
 * @param rules        The full, unsorted rule set (typically system preferences).
 * @returns The first matching enabled rule's color, or `null` when nothing
 *          matches — callers apply their own standard color for `null`.
 */
export function evaluateColor(
  row: Record<string, any> | null | undefined,
  targetField: string,
  rules: ColorRule[] | null | undefined
): string | null {
  if (!row || !rules || rules.length === 0) {
    return null;
  }

  const applicable = rules
    .filter((r) => r.enabled && Array.isArray(r.targetFields) && r.targetFields.includes(targetField))
    .sort((a, b) => a.order - b.order);

  for (const rule of applicable) {
    if (ruleMatches(rule, row[rule.sourceField])) {
      return rule.color;
    }
  }
  return null;
}

/**
 * Group-aware variant for the scheduler's grouping grids. A grouped cell spans
 * multiple rows; color it only when every row resolves to the same color,
 * otherwise fall back to the standard color (`null`). See the "Heterogeneous
 * group handling" section of the spec.
 */
export function evaluateGroupColor(
  rows: Array<Record<string, any>>,
  targetField: string,
  rules: ColorRule[] | null | undefined
): string | null {
  if (!rows || rows.length === 0) {
    return null;
  }
  const first = evaluateColor(rows[0], targetField, rules);
  if (first === null) {
    return null;
  }
  for (let i = 1; i < rows.length; i++) {
    if (evaluateColor(rows[i], targetField, rules) !== first) {
      return null;
    }
  }
  return first;
}
